import { Box, SxProps, Theme, Typography } from "@mui/material";
import { EditorEvents } from "@tiptap/core";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import {
	FontSize,
	MenuButtonBold,
	MenuButtonBulletedList,
	MenuButtonItalic,
	MenuButtonOrderedList,
	MenuButtonUnderline,
	MenuControlsContainer,
	MenuDivider,
	MenuSelectFontSize,
	MenuSelectHeading,
	RichTextEditor,
	RichTextEditorProps,
	RichTextReadOnly
} from "mui-tiptap";
import { ReactElement, forwardRef, useState } from "react";
import { zoullooColors } from "../../theme";

// Utility function to clean empty HTML content
export const cleanEmptyContent = (html: string): string => {
	// Remove empty paragraphs and whitespace-only content using safer approaches
	let cleaned = html
		.replace(/<p><\/p>/g, "") // Remove empty <p></p> tags
		.replace(/<p><\/p>/g, ""); // Run twice to catch any remaining

	// Remove paragraphs with only whitespace using string manipulation
	// Split by <p> tags and filter out empty or whitespace-only content
	const parts = cleaned.split(/<p>/);
	const filteredParts = parts.map(part => {
		if (part.includes("</p>")) {
			const content = part.split("</p>")[0];
			// Check if content is only whitespace
			if (content.trim() === "") {
				return part.replace(content + "</p>", "");
			}
		}
		return part;
	});

	cleaned = filteredParts.join("<p>");

	// Remove leading and trailing whitespace safely
	cleaned = cleaned.replace(/^\s/, "").replace(/\s$/, "");

	// Only return empty string if cleaning resulted in completely empty content
	// Otherwise, return the original HTML to preserve content
	return cleaned.trim() === "" ? "" : html;
};

export const getExtensions = (placeholder?: string) => [
	StarterKit,
	Link.configure({
		openOnClick: false
	}),
	Placeholder.configure({
		placeholder
	}),
	Underline,
	TextStyle,
	FontSize
];

export default forwardRef<
	HTMLElement,
	{
		readonly label?: string;
		readonly description?: ReactElement | string;
		readonly placeholder?: string;
		readonly readonly?: boolean;
		readonly sx?: SxProps<Theme>;
		readonly maxLength?: number;
		readonly showCounter?: boolean;
		readonly error?: boolean;
		/**
		 * Forward Tiptap update events to `onUpdate` only while the editor is focused (a genuine
		 * user edit). Non-focused updates are programmatic — they fire on mount/remount/content
		 * reset — and forwarding them lets an editor that just mounted with one language's text
		 * overwrite whatever value the parent now points at (e.g. after a language switch the
		 * outgoing editor emits and clobbers the newly-selected language). Defaults to false
		 * (every update forwarded) to preserve behaviour for all other usages.
		 */
		readonly emitUpdatesOnlyWhenFocused?: boolean;
	} & Omit<RichTextEditorProps, "extensions"> & {
			readonly extensions?: RichTextEditorProps["extensions"];
		}
>(function HtmlEditor(
	{ label, description, placeholder, readonly = false, sx, maxLength, showCounter = true, error, emitUpdatesOnlyWhenFocused = false, ...richTextEditorProps },
	ref
) {
	const [characterCount, setCharacterCount] = useState(0);

	// Track previous character count to detect if we should block input
	const [previousCount, setPreviousCount] = useState(0);

	// Enhanced rich text editor props with empty content handling
	const enhancedProps = {
		...richTextEditorProps,
		onUpdate: ({ editor, ...args }: EditorEvents["update"]) => {
			// Override the getHTML method to return cleaned content
			const originalGetHTML = editor.getHTML.bind(editor);
			editor.getHTML = () => {
				const html = originalGetHTML();
				return cleanEmptyContent(html);
			};

			// Update character count using getText (plain text without HTML tags)
			const textContent = editor.getText();
			const currentCount = textContent.length;

			// Block input if maxLength is exceeded - undo the last change
			if (maxLength && currentCount > maxLength && currentCount > previousCount) {
				editor.commands.undo();
				return; // Don't call the original onUpdate since we reverted
			}

			setCharacterCount(currentCount);
			setPreviousCount(currentCount);

			// A non-focused update is programmatic (mount/remount/content reset), never a user
			// edit. Forwarding it would let an editor that just mounted with one language's text
			// overwrite the value of whichever language the parent now points at. Opt-in per usage.
			if (emitUpdatesOnlyWhenFocused && !editor.isFocused) {
				return;
			}

			// Call the original onUpdate if it exists
			richTextEditorProps.onUpdate?.({ editor, ...args });
		},
		onCreate: ({ editor, ...args }: EditorEvents["create"]) => {
			// Override the getHTML method on creation as well
			const originalGetHTML = editor.getHTML.bind(editor);
			editor.getHTML = () => {
				const html = originalGetHTML();
				return cleanEmptyContent(html);
			};

			// Initialize character count
			const textContent = editor.getText();
			setCharacterCount(textContent.length);
			setPreviousCount(textContent.length);

			// Call the original onCreate if it exists
			richTextEditorProps.onCreate?.({ editor, ...args });
		}
	};

	// Calculate counter states
	const remaining = maxLength ? maxLength - characterCount : 0;
	const isNearLimit = maxLength ? remaining <= Math.ceil(maxLength * 0.1) : false;
	const isAtLimit = maxLength ? remaining <= 0 : false;

	return (
		<Box
			ref={ref}
			sx={{
				...sx,
				marginBottom: 2,
				"& .MuiTiptap-FieldContainer-root": {
					display: "grid",
					zIndex: 0,
					"&:hover": {
						"& > .MuiTiptap-FieldContainer-notchedOutline": {
							border: `1px solid ${zoullooColors.secondary.main} !important`
						}
					},
					"& .MuiTiptap-RichTextContent-root": {
						overflowY: "auto",
						resize: "vertical",
						transition: "all 2s, height 0s",
						"& .ProseMirror": {
							height: "200px"
						}
					}
				},
				".MuiTiptap-FieldContainer-focused > .MuiTiptap-FieldContainer-notchedOutline": {
					border: `1px solid ${zoullooColors.secondary.main} !important`
				},
				".MuiTiptap-FieldContainer-root > .MuiTiptap-FieldContainer-notchedOutline:hover":
					{
						border: 0
					}
			}}
		>
			{label && (
				<Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
					{label}
				</Typography>
			)}
			{description && (
				<Typography variant="body2" gutterBottom>
					{description}
				</Typography>
			)}
			{readonly ? (
				<RichTextReadOnly
					content={richTextEditorProps.content || ""}
					extensions={getExtensions(placeholder)}
				/>
			) : (
				<>
					<RichTextEditor
						{...enhancedProps}
						extensions={getExtensions(placeholder)}
						renderControls={() => (
							<MenuControlsContainer>
								<MenuButtonBold
									disableRipple
									tooltipLabel=""
									tooltipShortcutKeys={[]}
									sx={{ minWidth: "50px" }}
								/>
								<MenuButtonItalic
									disableRipple
									tooltipLabel=""
									tooltipShortcutKeys={[]}
									sx={{ minWidth: "50px" }}
								/>
								<MenuButtonUnderline
									disableRipple
									tooltipLabel=""
									tooltipShortcutKeys={[]}
									sx={{ minWidth: "50px" }}
								/>
								<MenuDivider />
								<MenuSelectFontSize tooltipTitle="" />
								<MenuDivider />
								<MenuSelectHeading tooltipTitle={""} />
								<MenuDivider />
								<MenuButtonBulletedList
									disableRipple
									tooltipLabel=""
									tooltipShortcutKeys={[]}
									sx={{ minWidth: "50px" }}
								/>
								<MenuButtonOrderedList
									disableRipple
									tooltipLabel=""
									tooltipShortcutKeys={[]}
									sx={{ minWidth: "50px" }}
								/>
							</MenuControlsContainer>
						)}
					/>
					{showCounter && maxLength && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								mt: 0.5,
								px: 1.5
							}}
						>
							<Typography
								variant="caption"
								sx={{
									color: error
										? "error.main"
										: isAtLimit
										? "error.main"
										: isNearLimit
										? "warning.main"
										: zoullooColors.grey[450],
									fontWeight: isNearLimit || isAtLimit ? 500 : 400
								}}
							>
								{characterCount}/{maxLength}
							</Typography>
						</Box>
					)}
				</>
			)}
		</Box>
	);
});
